import React from "react";
import {
  Heading,
  Flex,
  Button,
  IconButton,
  Link,
  Tag,
  TagLabel,
  Stack,
  Image,
  Icon,
} from "@chakra-ui/react";
import { FiEdit2, FiPlus } from "react-icons/fi";
import { RiDeleteBin5Line } from "react-icons/ri";
import { Coupon } from "../../fetchers/model";
import { useNavigate } from "react-router-dom";
import ConfirmationDialog from "../../utils/Confirmation";
import { useTranslation } from "react-i18next";
import { Table as AITable } from "../../utils/Table";
import axios from "axios";
import { Select } from "chakra-react-select";
import { FilterValue, SortBy } from "../../utils/CommonInterface";
import API from "../../config/api";
import { BsCardImage } from "react-icons/bs";

export default function ListCoupon() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [data, setData] = React.useState<Coupon[]>([]);
  const [pageCount, setPageCount] = React.useState(0);
  const fetchIdRef = React.useRef(0);

  const fetchData = React.useCallback(
    async ({
      pageSize,
      pageIndex,
      globalFilter,
      filters,
      sortBy,
    }: {
      pageSize: number;
      pageIndex: number;
      globalFilter: string;
      filters: FilterValue[];
      sortBy: SortBy[];
    }) => {
      const fetchId = ++fetchIdRef.current;

      setLoading(true);

      const statusFilters = filters.filter(
        (x) => x.id === "status" && x.value.length > 0
      );
      let statusParams =
        (statusFilters.length > 0 &&
          statusFilters[0].value.map((x) => `&status[]=${x}`).join("")) ||
        "";

      let sortByParam =
        (sortBy.length > 0 &&
          `&order=${sortBy[0].desc ? "-" : ""}${sortBy[0].id}`) ||
        "";

      const { data, headers } = await axios.get<Coupon[]>(
        `${API.COUPON}?page=${pageIndex + 1}&per_page=${pageSize}${
          globalFilter ? `&keyword=${globalFilter}` : ""
        }${statusParams}${sortByParam}`
      );
      if (fetchId === fetchIdRef.current) {
        setData(data);
        setPageCount(Math.ceil(parseInt(headers["total"]) / pageSize));

        setLoading(false);
      }
    },
    []
  );

  const statusOptions = ["activate", "deactivate"].map(function (val) {
    return {
      label: t(`coupon.status.${val}`),
      value: val,
    };
  });

  const tableColumns = [
    {
      Header: t("coupon.name"),
      accessor: "name" as const,
      disableFilters: true,
    },
    {
      Header: t("coupon.issuer"),
      accessor: "issuer" as const,
      disableFilters: true,
    },
    {
      Header: t("coupon.discount_money"),
      accessor: "discount_money" as const,
      disableFilters: true,
    },
    {
      Header: t("coupon.status.title"),
      accessor: "status" as const,
      FilterHeader: t("coupon.status.title"),
      Filter: ({ column }: { column: any }) => (
        <Select
          size="sm"
          isMulti
          name={column.id}
          closeMenuOnSelect={false}
          value={statusOptions.filter(
            (x) => column.filterValue && column.filterValue.includes(x.value)
          )}
          options={statusOptions}
          onChange={(option) => column.setFilter(option.map((x) => x.value))}
        />
      ),
      filterable: true,
    },
    {
      Header: "",
      accessor: "action" as const,
      disableSortBy: true,
      disableFilters: true,
    },
  ];

  const tableData = data.map((item) => ({
    name: (
      <Stack direction="row" spacing={2} alignItems="center">
        {item.logo && item.logo.id ? (
          <Image
            boxSize="36px"
            objectFit="cover"
            src={item.logo.url}
            alt={item.name}
          />
        ) : (
          <Icon as={BsCardImage} boxSize="36px" color={"gray.400"} />
        )}
        <Link
          color="blue.500"
          href={`/console/coupon/${item.id}`}
          fontWeight={"bold"}
        >
          {item.name}
        </Link>
      </Stack>
    ),
    issuer: item.issuer,
    discount_money:
      item.discount_type === "percentage"
        ? `${item.discount_money * 100}%`
        : item.discount_money,
    status: (
      <Tag
        size={"sm"}
        borderRadius="full"
        variant="solid"
        colorScheme={item.status === "activate" ? "blue" : "gray"}
      >
        <TagLabel>{t(`coupon.status.${item.status}`)}</TagLabel>
      </Tag>
    ),
    action: (
      <Stack direction={"row"} spacing={1} alignItems={"center"}>
        <IconButton
          icon={<FiEdit2 />}
          aria-label={t("action.edit")}
          variant="link"
          onClick={() => navigate(`/console/coupon/${item.id}/edit`)}
        />
        <ConfirmationDialog
          title={t("coupon.delete.title")}
          description={t("coupon.delete.description", {
            coupon_name: item.name,
          })}
          onConfirm={() => deleteItem(item.id)}
        >
          <IconButton
            icon={<RiDeleteBin5Line />}
            aria-label={t("action.delete")}
            variant="link"
          />
        </ConfirmationDialog>
      </Stack>
    ),
  }));

  const deleteItem = async (itemId: string) => {
    await axios.delete<void>(`${API.COUPON}/${itemId}`);
    setData(data.filter((x) => x.id !== itemId));
  };

  return (
    <>
      <Flex justifyContent="space-between">
        <Heading as="h4" size="lg" mb="4">
          {t("coupon.title.name")}
        </Heading>
        <Button
          leftIcon={<FiPlus />}
          colorScheme="blue"
          variant="solid"
          onClick={() => navigate("/console/coupon/new")}
        >
          {t("action.new")}
        </Button>
      </Flex>
      <AITable
        columns={tableColumns}
        data={tableData}
        fetchData={fetchData}
        loading={loading}
        pageCount={pageCount}
        useSearch={true}
        disableFilters={false}
        disableSortBy={false}
      />
    </>
  );
}
